import { Box, Chip, Typography } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useRef, useState, useEffect } from "react";
import { BaseModalComponent } from "../../../../../../../common/BaseModalComponent";
import { GroupedEventCard } from "./GroupedEventCard";
import { getStatusPalette } from "../../helpers/normalizedColorsCalendar";

const STATUS_LABELS = {
  scheduled: "Scheduled",
  inProgress: "In progress",
  completed: "Completed",
  cancelled: "Canceled",
};

export const GroupedDayCard = ({ date, events }) => {
  const triggerRef = useRef(null);
  const modalKeyRef = useRef(`Grouped-${date}`);
  const gridRef = useRef(null);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [metrics, setMetrics] = useState({ width: 120, height: 90 });

  useEffect(() => {
    if (!gridRef.current) return;

    const ro = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setMetrics({ width: rect.width, height: rect.height });
    });

    ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (selectedStatus && triggerRef.current) triggerRef.current.click();
  }, [selectedStatus]);

  const summary = events.reduce((acc, ev) => {
    const s = ev.extendedProps?.status;
    if (!s) return acc;
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const handleStatusClick = (e, statusKey) => {
    if (!summary[statusKey]) return;
    e.preventDefault();
    e.stopPropagation();

    setSelectedStatus(null);
    setTimeout(() => setSelectedStatus(statusKey), 0);
  };

  const filteredEvents = selectedStatus
    ? events.filter((ev) => ev.extendedProps?.status === selectedStatus).sort((a, b) => new Date(a.start) - new Date(b.start))
    : [];

  const selectedStatusLabel = STATUS_LABELS[selectedStatus] || "";

  const fontSize = Math.max(0.38, Math.min(metrics.width * 0.010, 0.60)) + "rem";

  const iconSize = Math.max(6, Math.min(metrics.width * 0.016, 11));

  const chipHeight = Math.max(16, Math.min(metrics.height * 0.25, 26));

  const horizontalGap = Math.min(metrics.width * 0.05, 6);

  return (
    <>
      <Box
        ref={gridRef}
        sx={{
          mt: 1,
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: 0.7,
          overflow: "hidden", 
          minWidth: 0, 
        }}
      >
        {Object.keys(STATUS_LABELS).map((statusKey) => {
          const label = STATUS_LABELS[statusKey];
          const count = summary[statusKey] || 0;
          const enabled = count > 0;

          const palette = getStatusPalette(statusKey);

          return (
            <Chip
              key={statusKey}
              onClick={(e) => enabled && handleStatusClick(e, statusKey)}
              clickable={enabled}
              icon={
                <FiberManualRecordIcon
                  sx={{
                    fontSize: iconSize,
                    color: "#fff !important",
                    ml: 0.4,
                    flexShrink: 0, 
                  }}
                />
              }
              sx={{
                height: chipHeight,
                borderRadius: 2,
                bgcolor: palette.color,
                color: "#fff",
                cursor: enabled ? "pointer" : "default",
                opacity: enabled ? 1 : 0.33,
                fontWeight: 700,
                transition: "0.15s ease",

                minWidth: 0, 
                maxWidth: "100%", 
                overflow: "hidden", 
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",

                "& .MuiChip-label": {
                  px: 0.8,
                  fontSize,
                  display: "flex",
                  alignItems: "center",
                  gap: horizontalGap + "px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },

                "&.MuiChip-clickable:hover": enabled
                  ? {
                      transform: "scale(1.04)",
                      boxShadow: `0 0 0 2px ${palette.color}40`,
                      bgcolor: palette.color,
                    }
                  : {
                      bgcolor: palette.color + " !important",
                      transform: "none !important",
                      boxShadow: "none !important",
                    },
              }}
              label={`${label} • ${count}`}
            />
          );
        })}
      </Box>

      <BaseModalComponent
        defaultSizes={{ width: "600", height: "800" }}
        modalLabel={selectedStatus ? `Jobs ${selectedStatusLabel} from ${date}` : `Jobs from ${date}`}
        modalKey={modalKeyRef.current}
        elementTrigger={
          <button
            ref={triggerRef}
            type="button"
            style={{
              position: "absolute",
              left: "-99999px",
              top: "-99999px",
            }}
          />
        }
      >
        <Box sx={{ p: 2 }}>
          {filteredEvents.length === 0 ? (
            <Typography sx={{ opacity: 0.6 }}>No events.</Typography>
          ) : (
            filteredEvents.map((e) => <GroupedEventCard key={e.id} event={e} />)
          )}
        </Box>
      </BaseModalComponent>
    </>
  );
};
